import React from 'react';
import { StanButton } from '../StanButton/StanButton';
import { createIconElement } from '@/lib';
import { cn } from '@/lib/utils/cn';
import { ButtonProps, StanIcon } from '../types';
import IcArrowRight from '@/assets/icons/ic-arrow-right.svg';
import styles from './StanBanner.module.css';

export type BannerVariant = 'info' | 'warning' | 'stan-info' | 'danger';

export type BannerButton = Omit<ButtonProps, 'loading' | 'children'> & {
    // Button text is used as both the name (for analytics) and display text
    name: string;
};

export interface StanBannerProps {
    icon?: StanIcon;
    title?: string;
    subtitle?: string;
    rightIcon?: StanIcon;
    variant?: BannerVariant;
    buttons?: BannerButton[];
    ctaLoading?: boolean;
    onClick?: () => void;
    ctaText?: string; // CTA link text (renders as link button on desktop, whole banner clickable on mobile)
    children?: React.ReactNode;
    className?: string;
}

export const StanBanner = React.memo<StanBannerProps>(
    ({
        icon,
        title,
        subtitle,
        rightIcon,
        variant = 'info',
        buttons = [],
        ctaLoading = false,
        onClick,
        ctaText,
        children,
        className,
    }) => {
        const hasButtons = buttons.length > 0;
        const hasCtaText = !!ctaText;
        const isClickable = !hasButtons && (!!onClick || hasCtaText);
        // Only show rightIcon if explicitly provided or if there are buttons (for backward compatibility)
        const showRightIcon = rightIcon !== undefined ? rightIcon : hasButtons ? IcArrowRight : undefined;
        // Simple text-only mode: if no title/subtitle/icon/buttons, just render children as text
        const isSimpleMode = !title && !subtitle && !icon && !hasButtons && !showRightIcon && !hasCtaText;

        const handleBannerClick = (e: React.MouseEvent<HTMLDivElement>) => {
            // On mobile with CTA, whole banner is clickable
            // On desktop, only trigger if clicking the banner itself (not buttons or interactive children)
            if (hasCtaText) {
                // Check if we're on mobile (CTA link is hidden on mobile)
                const target = e.target as HTMLElement;
                const isClickingCtaLink = target.closest(`.${styles['cta-link']}`);
                // On mobile, CTA link is hidden, so whole banner is clickable
                // On desktop, only trigger if not clicking the CTA link
                if (!isClickingCtaLink) {
                    onClick?.();
                }
            } else if (isClickable && e.target === e.currentTarget) {
                onClick?.();
            }
        };

        const handleContentClick = () => {
            if (isClickable || hasCtaText) {
                onClick?.();
            }
        };

        // Map variant to CSS class name (stan-info -> stanInfo for CSS modules)
        const variantClass = variant === 'stan-info' ? 'stanInfo' : variant;

        const bannerClasses = cn(
            styles['stan-banner'],
            styles[variantClass],
            isClickable && styles['cursor-pointer'],
            hasCtaText && styles['has-cta'],
            className
        );

        // Simple text-only mode (like WarningHeader)
        if (isSimpleMode && children) {
            return (
                <div className={bannerClasses} onClick={handleBannerClick}>
                    <h5 className={cn(styles['text-container'], styles['simple-text'])} onClick={handleContentClick}>
                        {children}
                    </h5>
                </div>
            );
        }

        return (
            <div className={bannerClasses} onClick={handleBannerClick}>
                <div className={styles['banner-content']}>
                    {icon && (
                        <span className={styles['banner-icon']} onClick={handleContentClick}>
                            {createIconElement(icon, {
                                className: styles['icon-svg'],
                            })}
                        </span>
                    )}

                    <div className={styles['text-container']} onClick={handleContentClick}>
                        {title && <span className={cn(styles.title, 'h4', 'text-bold')}>{title}</span>}
                        {subtitle && <span className={cn(styles.subtitle, 'para-2')}>{subtitle}</span>}
                        {!title && !subtitle && children && <span>{children}</span>}
                    </div>

                    {children && (title || subtitle) && <div id='slot-container'>{children}</div>}

                    <div className={styles['button-container']}>
                        {hasButtons &&
                            buttons.map((button, index) => (
                                <StanButton
                                    key={index}
                                    name={`app-banner-cta-${button.name}`}
                                    onClick={button.onClick}
                                    color={button.color || 'primary'}
                                    variant={button.variant}
                                    size={button.size || 'md'}
                                    disabled={button.disabled}
                                    type={button.type}
                                    loading={ctaLoading}
                                    className={cn(styles['integration-button'], button.className)}
                                    icon={button.icon}
                                >
                                    {button.name}
                                </StanButton>
                            ))}

                        {!hasButtons && hasCtaText && (
                            <StanButton
                                name='app-banner-cta-link'
                                onClick={e => {
                                    e.stopPropagation();
                                    onClick?.();
                                }}
                                variant='link'
                                color='dark'
                                size='md'
                                className={cn(styles['cta-link'], 'max-md:hidden')}
                            >
                                {ctaText}
                            </StanButton>
                        )}

                        {!hasButtons && !hasCtaText && showRightIcon && (
                            <div className={styles['action-icon']} onClick={handleContentClick}>
                                {createIconElement(showRightIcon, {
                                    className: styles['icon-svg'],
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

StanBanner.displayName = 'StanBanner';
